import { ArrowUpRight, Award, Quote, Star } from "lucide-react";
import { motion } from "framer-motion";
import { SITE } from "@/lib/site";
import { ThemeBar, PreviewFooter } from "@/pages/preview/MidnightPreview";

/* ───────────────── EDITORIAL PREMIUM (cream + deep navy + Playfair) ─────────────── */

const NAVY = "#0B1F3A";
const GOLD = "#8B6F2E";
const CREAM = "#FAF7F2";
const PAPER = "#FFFFFF";
const MUTED = "#475569";
const SERIF = "'Playfair Display', 'Cormorant Garamond', serif";
const SERIF_ITALIC = "'Playfair Display', serif";
const BODY = "'Cormorant Garamond', 'IBM Plex Sans', serif";
const SANS = "'IBM Plex Sans', sans-serif";

export default function EditorialPreview() {
  return (
    <div className="min-h-screen" style={{ background: CREAM, color: NAVY, fontFamily: BODY }} data-testid="preview-editorial">
      <ThemeBar variant="editorial" />

      {/* Header band */}
      <header className="border-b" style={{ borderColor: "rgba(11,31,58,0.15)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 grid place-items-center rounded-full font-bold text-sm" style={{ background: NAVY, color: CREAM, fontFamily: SERIF }}>
              AS
            </div>
            <div>
              <div className="font-bold text-base leading-none" style={{ fontFamily: SERIF, color: NAVY }}>Ajay Sahni</div>
              <div className="text-[10px] uppercase tracking-[0.28em] mt-1" style={{ color: MUTED, fontFamily: SANS }}>aksahni.com</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm" style={{ fontFamily: SANS, color: NAVY }}>
            <a className="hover:underline underline-offset-4">Expertise</a>
            <a className="hover:underline underline-offset-4">Programs</a>
            <a className="hover:underline underline-offset-4">Voices</a>
            <a className="hover:underline underline-offset-4">Insights</a>
            <a className="hover:underline underline-offset-4">Contact</a>
          </nav>
          <button className="text-sm px-5 py-2.5 rounded-full" style={{ background: NAVY, color: CREAM, fontFamily: SANS }}>
            Book a Call
          </button>
        </div>
      </header>

      {/* Hero — magazine-style asymmetric */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-20 md:pt-24 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.32em] mb-6" style={{ color: GOLD, fontFamily: SANS }}>
              ─── Vol. I · The Practice
            </div>
            <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
              className="text-5xl sm:text-6xl md:text-[5.5rem] leading-[0.98] tracking-tight"
              style={{ fontFamily: SERIF, color: NAVY }}
            >
              Engineering trust <br />
              <em style={{ fontFamily: SERIF_ITALIC, fontStyle: "italic", color: GOLD }}>at the speed</em> <br />
              of cloud.
            </motion.h1>
            <p className="mt-8 text-xl leading-relaxed max-w-xl" style={{ color: MUTED, fontFamily: BODY }}>
              Ajay Sahni is a senior corporate trainer and consultant — twenty
              years in IT, fifteen years on the lectern — helping Fortune 500
              teams navigate Microsoft, Cloud, Cybersecurity and AI Security
              with deliberate craft.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 items-start">
              <button className="inline-flex items-center gap-2 px-7 py-3.5 text-sm" style={{ background: NAVY, color: CREAM, fontFamily: SANS, letterSpacing: "0.05em" }}>
                BOOK A DISCOVERY CALL <ArrowUpRight size={15} />
              </button>
              <button className="inline-flex items-center gap-2 px-7 py-3.5 text-sm border" style={{ borderColor: NAVY, color: NAVY, fontFamily: SANS, letterSpacing: "0.05em" }}>
                EXPLORE PROGRAMS
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative">
              <div className="absolute -inset-6 rounded-md" style={{ background: GOLD, transform: "rotate(-2deg)", opacity: 0.18 }} />
              <div className="relative overflow-hidden" style={{ borderRadius: "4px", border: `1px solid ${NAVY}` }}>
                <img src={SITE.portrait} alt="Ajay Sahni" className="w-full aspect-[4/5] object-cover object-top" style={{ filter: "grayscale(0.08) contrast(1.05)" }} />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: GOLD, fontFamily: SANS }}>Plate I</div>
                <div className="italic text-sm" style={{ fontFamily: SERIF_ITALIC, color: NAVY }}>
                  "{SITE.tagline}"
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trusted-by editorial strip */}
        <div className="border-t border-b" style={{ borderColor: "rgba(11,31,58,0.15)", background: PAPER }}>
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 grid grid-cols-2 md:grid-cols-5 gap-6 items-center">
            <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: NAVY, fontFamily: SANS }}>Trusted by global teams</div>
            {["Microsoft", "Intel", "IBM", "Saudi Aramco"].map((n) => (
              <div key={n} className="text-center font-semibold" style={{ fontFamily: SERIF, color: NAVY, opacity: 0.7 }}>{n}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats — editorial style */}
      <section className="py-20" style={{ background: CREAM }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {[["20+","Years in IT"],["15+","Years training"],["10,000+","Pros trained"],["50+","Enterprise clients"]].map(([v,l]) => (
            <div key={l} className="border-t pt-5" style={{ borderColor: NAVY }}>
              <div className="text-5xl md:text-6xl" style={{ fontFamily: SERIF, color: NAVY }}>{v}</div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.28em]" style={{ color: MUTED, fontFamily: SANS }}>{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Expertise — editorial column treatment */}
      <section className="py-24" style={{ background: PAPER }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.32em] mb-4" style={{ color: GOLD, fontFamily: SANS }}>01 — Practice</div>
            <h2 className="text-4xl md:text-5xl leading-[1.05]" style={{ fontFamily: SERIF, color: NAVY }}>
              A single practice across <em style={{ fontStyle: "italic" }}>seven</em> disciplines.
            </h2>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {[
              { t: "Cloud Architecture", b: "Designing production-grade landing zones across Azure, AWS and Google Cloud with security baked in from day one." },
              { t: "Cybersecurity", b: "Strategic and tactical security workshops mapped to CISSP, CCSP, CEH and Security+." },
              { t: "AI Security & GenAI", b: "Securing the next decade of AI workloads — from prompt-injection to model exfiltration." },
              { t: "Microsoft Stack", b: "Entra ID, Intune, M365, SCCM, Active Directory, Teams, SharePoint — end-to-end modernisation." },
            ].map((c) => (
              <div key={c.t}>
                <h3 className="text-2xl mb-2" style={{ fontFamily: SERIF, color: NAVY }}>{c.t}</h3>
                <p className="text-base leading-relaxed" style={{ color: MUTED, fontFamily: BODY }}>{c.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications — paper-style stamps */}
      <section className="py-24" style={{ background: CREAM }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-[10px] uppercase tracking-[0.32em] mb-4" style={{ color: GOLD, fontFamily: SANS }}>02 — Credentials</div>
          <h2 className="text-4xl md:text-5xl leading-[1.05] max-w-2xl" style={{ fontFamily: SERIF, color: NAVY }}>
            A vocabulary your enterprise <em style={{ fontStyle: "italic" }}>already speaks.</em>
          </h2>
          <div className="mt-12 grid grid-cols-3 md:grid-cols-6 gap-3">
            {["CISSP","CCSP","CEH","Security+","AZ-104","AZ-305","AZ-500","SC-100","SC-200","MS-102","ITIL 4","CCSK"].map((c) => (
              <div key={c} className="aspect-square border flex flex-col items-center justify-center text-center p-3" style={{ borderColor: NAVY, background: PAPER }}>
                <Award size={14} style={{ color: GOLD }} />
                <div className="mt-2 text-lg" style={{ fontFamily: SERIF, color: NAVY }}>{c}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured testimonial */}
      <section className="py-24" style={{ background: PAPER }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Quote size={32} style={{ color: GOLD }} className="mx-auto" />
          <blockquote className="mt-6 text-2xl md:text-3xl leading-[1.4] italic" style={{ fontFamily: SERIF_ITALIC, color: NAVY }}>
            "Your passion for the subject is evident and has greatly enhanced
            my learning experience. The way you explain complex concepts with
            clarity and patience has been particularly helpful."
          </blockquote>
          <div className="mt-8 flex items-center justify-center gap-0.5" style={{ color: GOLD }}>
            {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" strokeWidth={0} />)}
          </div>
          <div className="mt-3 text-sm" style={{ color: NAVY, fontFamily: SERIF }}>
            <strong>Pierre H. Ndongue</strong>
          </div>
          <div className="text-[10px] uppercase tracking-[0.32em] mt-1" style={{ color: MUTED, fontFamily: SANS }}>
            Microsoft Azure Administration · Milwaukee, US
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-24" style={{ background: NAVY, color: CREAM }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="text-4xl md:text-6xl leading-[1.05]" style={{ fontFamily: SERIF }}>
            Shall we begin?
          </h2>
          <p className="mt-6 text-lg max-w-xl mx-auto leading-relaxed" style={{ color: "rgba(250,247,242,0.7)", fontFamily: BODY }}>
            Share your team's goals and I'll come back with a tailored proposal within 24 hours.
          </p>
          <button className="mt-10 inline-flex items-center gap-2 px-8 py-3.5 text-sm" style={{ background: CREAM, color: NAVY, fontFamily: SANS, letterSpacing: "0.08em" }}>
            BOOK A DISCOVERY CALL <ArrowUpRight size={15} />
          </button>
        </div>
      </section>

      <PreviewFooter variant="editorial" />
    </div>
  );
}
